(() => {
  'use strict';

  const initialized = new WeakSet();

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  };

  const jsonRoute = (url, fallback) => {
    const route = String(url || fallback || '');
    return route.endsWith('.js') ? route : `${route.replace(/\/$/, '')}.js`;
  };

  const readableError = async (response, fallback) => {
    const payload = await response.json().catch(() => ({}));
    const errors = payload?.errors;
    if (typeof errors === 'string') return errors;
    if (errors && typeof errors === 'object') return Object.values(errors).flat().join(' ');
    return payload?.description || payload?.message || fallback;
  };

  const formatMoney = (cents, currency = 'EGP') => {
    const amount = Number(cents || 0) / 100;
    if (String(currency).toUpperCase() === 'EGP') {
      return `LE ${amount.toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EGP',
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const mediaUrl = (url) => String(url || '').startsWith('//') ? `${window.location.protocol}${url}` : String(url || '');

  const initialize = (drawer) => {
    if (!drawer || initialized.has(drawer)) return;
    initialized.add(drawer);

    const content = drawer.querySelector('[data-cart-content]');
    const lines = drawer.querySelector('[data-cart-lines]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const count = drawer.querySelector('[data-cart-drawer-count]');
    const shipping = drawer.querySelector('[data-cart-shipping]');
    const shippingCopy = drawer.querySelector('[data-cart-shipping-copy]');
    const shippingValue = drawer.querySelector('[data-cart-shipping-value]');
    const shippingBar = drawer.querySelector('[data-cart-shipping-bar]');
    const discountRow = drawer.querySelector('[data-cart-discount-row]');
    const discountAmount = drawer.querySelector('[data-cart-discounts]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const checkoutTotal = drawer.querySelector('[data-cart-checkout-total]');
    const note = drawer.querySelector('[data-cart-note]');
    const status = drawer.querySelector('[data-cart-status]');
    const errorBox = drawer.querySelector('[data-cart-error]');
    const errorCopy = drawer.querySelector('[data-cart-error-copy]');
    const initialState = drawer.querySelector('[data-cart-initial-state]');
    const threshold = Math.max(1, Number(drawer.dataset.shippingThreshold || 150000));
    const cartUrl = jsonRoute(drawer.dataset.cartUrl, '/cart');
    const changeUrl = jsonRoute(drawer.dataset.cartChangeUrl, '/cart/change');
    const updateUrl = jsonRoute(drawer.dataset.cartUpdateUrl, '/cart/update');
    const itemsSection = drawer.dataset.cartItemsSection || 'siwa-cart-drawer-items';
    let cartState = null;
    try {
      cartState = JSON.parse(initialState?.textContent || 'null');
    } catch (_error) {
      cartState = null;
    }
    let busy = false;
    let retryAction = null;
    let searchIndex = null;

    if (!content || !lines || !empty || !footer) return;

    const announce = (message) => {
      if (!status) return;
      status.textContent = '';
      window.requestAnimationFrame(() => { status.textContent = message; });
    };

    const syncHeaderCount = (cart) => {
      const itemCount = Number(cart?.item_count || 0);
      document.querySelectorAll('.siwa-header__count').forEach((element) => {
        element.textContent = String(itemCount);
      });
      document.querySelectorAll('.siwa-header__bag').forEach((bag) => {
        bag.setAttribute('aria-label', `Bag, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
      });
      if (count) count.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    };

    const hideError = () => {
      if (errorBox) errorBox.hidden = true;
      retryAction = null;
    };

    const showError = (message, retry) => {
      if (errorCopy) errorCopy.textContent = message || 'We could not update your bag.';
      if (errorBox) errorBox.hidden = false;
      retryAction = retry || null;
      announce(message || 'We could not update your bag.');
    };

    const setBusy = (updating) => {
      busy = updating;
      content.classList.toggle('is-updating', updating && Boolean(cartState));
      content.setAttribute('aria-busy', String(updating));
      drawer.querySelectorAll('button, input, textarea').forEach((control) => {
        if (!control.matches('[data-cart-close], [data-cart-retry]')) control.disabled = updating;
      });
    };

    const renderShipping = (cart) => {
      const total = Number(cart?.total_price || 0);
      const remaining = Math.max(0, threshold - total);
      const percent = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
      const currency = cart?.currency || drawer.dataset.currency || 'EGP';
      if (shippingCopy) {
        shippingCopy.replaceChildren();
        if (remaining > 0) {
          shippingCopy.append('Add ', createElement('strong', '', formatMoney(remaining, currency)), ' to unlock free shipping.');
        } else {
          shippingCopy.append(createElement('strong', '', 'Free shipping unlocked.'));
        }
      }
      if (shippingValue) shippingValue.textContent = `${percent}%`;
      if (shippingBar) shippingBar.style.width = `${percent}%`;
      if (shipping) {
        shipping.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
        shipping.setAttribute('aria-valuetext', remaining > 0
          ? `${formatMoney(remaining, currency)} remaining for free shipping`
          : 'Free shipping unlocked');
      }
    };

    const addProperty = (container, key, value) => {
      const property = createElement('dl', 'siwa-cart-drawer__property');
      property.append(createElement('dt', '', key), createElement('dd', '', value));
      container.append(property);
    };

    const indexedContextFor = (item) => {
      if (!searchIndex) {
        try {
          searchIndex = JSON.parse(document.querySelector('[data-siwa-search-index]')?.textContent || '[]');
        } catch (_error) {
          searchIndex = [];
        }
      }
      let itemPath = '';
      try { itemPath = new URL(item.url || '', window.location.origin).pathname; } catch (_error) { itemPath = ''; }
      return searchIndex.find((entry) => {
        try { return new URL(entry.url || '', window.location.origin).pathname === itemPath; } catch (_error) { return false; }
      })?.context || '';
    };

    const createTrashIcon = () => {
      const namespace = 'http://www.w3.org/2000/svg';
      const icon = document.createElementNS(namespace, 'svg');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      const path = document.createElementNS(namespace, 'path');
      path.setAttribute('d', 'M4.75 7.25h14.5M9 4.75h6m-8 2.5.75 12h8.5l.75-12M10 10.25v6.5m4-6.5v6.5');
      icon.append(path);
      return icon;
    };

    const parseSectionLines = (html) => {
      if (typeof html !== 'string' || !html.trim()) return null;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fragment = parsed.querySelector('[data-cart-lines-fragment]');
      if (!fragment) return null;
      return [...fragment.children].map((child) => document.importNode(child, true));
    };

    const sectionRequestUrl = () => {
      const rootPath = window.Shopify?.routes?.root || '/';
      const url = new URL(rootPath, window.location.origin);
      url.searchParams.set('sections', itemsSection);
      return `${url.pathname}${url.search}`;
    };

    const fetchLinesSection = async () => {
      const response = await fetch(sectionRequestUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Your bag details could not be refreshed.');
      const payload = await response.json();
      const html = payload?.[itemsSection];
      if (typeof html !== 'string') throw new Error('Your bag details could not be refreshed.');
      return html;
    };

    const createCartLine = (item, currency) => {
      const line = createElement('li', 'siwa-cart-drawer__line');
      line.dataset.cartLine = '';
      line.dataset.cartKey = item.key;

      const media = createElement('a', 'siwa-cart-drawer__media');
      media.href = item.url || '#';
      media.setAttribute('aria-label', `View ${item.product_title || 'fragrance'}`);
      if (item.image) {
        const image = createElement('img');
        image.src = mediaUrl(item.image);
        image.alt = item.featured_image?.alt || item.product_title || '';
        image.width = 184;
        image.height = 230;
        image.loading = 'eager';
        media.append(image);
      } else {
        media.append(createElement('span', '', 'SIWA'));
      }

      const main = createElement('div', 'siwa-cart-drawer__line-main');
      const heading = createElement('div', 'siwa-cart-drawer__line-heading');
      const title = createElement('a', '', item.product_title || item.title || 'Siwa fragrance');
      title.href = item.url || '#';
      const price = createElement('div', 'siwa-cart-drawer__price');
      const referenceLine = Math.max(Number(item.original_line_price || 0), Number(item.final_line_price || 0));
      if (referenceLine > Number(item.final_line_price || 0)) {
        const original = createElement('s', '', formatMoney(referenceLine, currency));
        original.setAttribute('aria-label', 'Original price');
        price.append(original);
      }
      const current = createElement('strong', '', formatMoney(item.final_line_price, currency));
      current.setAttribute('aria-label', 'Current price');
      price.append(current);
      if (referenceLine > Number(item.final_line_price || 0)) {
        price.append(createElement('span', 'siwa-cart-drawer__saving', `Save ${formatMoney(referenceLine - Number(item.final_line_price || 0), currency)}`));
      }
      heading.append(title, price);
      main.append(heading);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        main.append(createElement('p', 'siwa-cart-drawer__variant', item.variant_title));
      }
      const planName = item.selling_plan_allocation?.selling_plan?.name;
      if (planName) main.append(createElement('p', 'siwa-cart-drawer__selling-plan', planName));

      const context = indexedContextFor(item);
      if (context) {
        const inspiration = createElement('p', 'siwa-cart-drawer__inspiration');
        if (/^inspired by\s+/i.test(context)) {
          inspiration.append(createElement('span', '', 'Inspired by'), createElement('strong', '', context.replace(/^inspired by\s+/i, '')));
        } else if (/^siwa original/i.test(context)) {
          inspiration.append(createElement('span', '', 'Siwa original composition'));
        }
        if (inspiration.childNodes.length) main.append(inspiration);
      }

      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (!key || key.startsWith('_') || value === null || value === '') return;
        const visibleValue = typeof value === 'string' ? value : JSON.stringify(value);
        addProperty(main, key, visibleValue);
      });

      const allocations = Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
      if (allocations.length) {
        const discounts = createElement('ul', 'siwa-cart-drawer__discounts');
        allocations.forEach((allocation) => {
          const label = allocation.discount_application?.title || 'Discount';
          const discount = createElement('li');
          discount.append(createElement('span', '', label), createElement('strong', '', `−${formatMoney(allocation.amount, currency)}`));
          discounts.append(discount);
        });
        main.append(discounts);
      }

      const actions = createElement('div', 'siwa-cart-drawer__line-actions');
      const quantity = createElement('div', 'siwa-cart-drawer__quantity');
      const minus = createElement('button', '', '−');
      minus.type = 'button';
      minus.dataset.cartQuantityMinus = '';
      minus.dataset.cartKey = item.key;
      minus.setAttribute('aria-label', `Decrease ${item.product_title || 'item'} quantity`);

      const input = createElement('input');
      input.type = 'number';
      input.min = '0';
      input.inputMode = 'numeric';
      input.value = String(item.quantity || 0);
      input.dataset.cartQuantity = '';
      input.dataset.cartKey = item.key;
      input.setAttribute('aria-label', `Quantity for ${item.product_title || 'item'}`);

      const plus = createElement('button', '', '+');
      plus.type = 'button';
      plus.dataset.cartQuantityPlus = '';
      plus.dataset.cartKey = item.key;
      plus.setAttribute('aria-label', `Increase ${item.product_title || 'item'} quantity`);
      quantity.append(minus, input, plus);

      const remove = createElement('button', 'siwa-cart-drawer__remove');
      remove.type = 'button';
      remove.dataset.cartRemove = '';
      remove.dataset.cartKey = item.key;
      remove.setAttribute('aria-label', `Remove ${item.product_title || 'item'} from your bag`);
      remove.append(createTrashIcon());
      actions.append(quantity, remove);
      main.append(actions);

      const lineStatus = createElement('p', 'siwa-cart-drawer__line-status');
      lineStatus.dataset.cartLineStatus = '';
      lineStatus.setAttribute('role', 'status');
      main.append(lineStatus);

      if (document.body.dataset.siwaLoyaltyEnabled === 'true') {
        const loyalty = createElement('div', 'siwa-loyalty-estimate siwa-loyalty-estimate--line');
        loyalty.dataset.siwaLoyaltyEstimate = '';
        loyalty.dataset.loyaltyContext = 'line';
        loyalty.dataset.loyaltyCents = String(item.final_line_price || 0);
        loyalty.dataset.loyaltyMultiplierBasis = String(Math.round(Number(window.SiwaLoyalty?.config?.multipliers?.[window.SiwaLoyalty?.config?.tier] || 1) * 100));
        const loyaltyMark = createElement('span', 'siwa-loyalty-estimate__mark', '✦');
        const loyaltyCopy = createElement('span');
        const loyaltyStrong = createElement('strong');
        loyaltyStrong.append('Earn ', createElement('span', '', window.SiwaLoyalty?.pointsFor?.(item.final_line_price) || Math.floor(Number(item.final_line_price || 0) / 100)), ' points');
        loyaltyCopy.append(loyaltyStrong);
        loyalty.append(loyaltyMark, loyaltyCopy);
        main.insertBefore(loyalty, actions);
      }
      line.append(media, main);
      return line;
    };

    const render = (cart, sectionHtml = null) => {
      cartState = cart;
      hideError();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const currency = cart?.currency || drawer.dataset.currency || 'EGP';
      syncHeaderCount(cart);
      renderShipping(cart);
      const sectionLines = parseSectionLines(sectionHtml);
      lines.replaceChildren(...(sectionLines || items.map((item) => createCartLine(item, currency))));
      lines.hidden = items.length === 0;
      empty.hidden = items.length > 0;
      footer.hidden = items.length === 0;

      if (items.length) {
        const totalDiscount = Number(cart.total_discount || 0);
        if (discountRow) discountRow.hidden = totalDiscount <= 0;
        if (discountAmount) discountAmount.textContent = `−${formatMoney(totalDiscount, currency)}`;
        if (subtotal) subtotal.textContent = formatMoney(cart.total_price, currency);
        if (checkoutTotal) checkoutTotal.textContent = formatMoney(cart.total_price, currency);
        if (note && document.activeElement !== note) note.value = cart.note || '';
      }
      document.dispatchEvent(new CustomEvent('siwa:loyalty:cart-rendered', { detail: { cart } }));
      setBusy(false);
    };

    const renderWithSection = async (cart, sectionHtml = null) => {
      let authoritativeHtml = sectionHtml;
      if (!parseSectionLines(authoritativeHtml)) {
        authoritativeHtml = await fetchLinesSection().catch(() => null);
      }
      render(cart, authoritativeHtml);
      return cart;
    };

    const fetchCart = async () => {
      hideError();
      setBusy(true);
      try {
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!response.ok) throw new Error(await readableError(response, 'Your bag could not be loaded.'));
        const cart = await response.json();
        const sectionHtml = await fetchLinesSection().catch(() => null);
        render(cart, sectionHtml);
        return cart;
      } catch (error) {
        setBusy(false);
        showError(error.message || 'Your bag could not be loaded.', fetchCart);
        return null;
      }
    };

    const changeLine = async (key, quantity, actionLabel) => {
      if (!key || busy) return;
      const requestedQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
      const previousCart = cartState;
      hideError();
      setBusy(true);
      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: key,
            quantity: requestedQuantity,
            sections: itemsSection,
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error(await readableError(response, 'That quantity is not available.'));
        const cart = await response.json();
        await renderWithSection(cart, cart.sections?.[itemsSection]);
        const removedName = previousCart?.items?.find((item) => item.key === key)?.product_title || 'Item';
        announce(requestedQuantity === 0 ? `${removedName} removed from your bag.` : 'Quantity updated.');
        document.dispatchEvent(new CustomEvent('siwa:cart:updated', { detail: { cart, open: false, source: 'drawer' } }));
        const nextLine = lines.querySelector(`[data-cart-key="${CSS.escape(key)}"]`);
        const preferred = nextLine?.querySelector(actionLabel === 'minus' ? '[data-cart-quantity-minus]' : '[data-cart-quantity-plus]');
        (preferred || nextLine?.querySelector('button') || drawer.querySelector('[data-cart-close]'))?.focus({ preventScroll: true });
      } catch (error) {
        if (previousCart) render(previousCart);
        else setBusy(false);
        showError(error.message || 'That quantity is not available.', () => changeLine(key, requestedQuantity, actionLabel));
      }
    };

    const saveNote = async () => {
      if (!note || busy) return;
      hideError();
      setBusy(true);
      try {
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            note: note.value,
            sections: itemsSection,
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error(await readableError(response, 'Your note could not be saved.'));
        const cart = await response.json();
        await renderWithSection(cart, cart.sections?.[itemsSection]);
        announce('Order note saved.');
        drawer.querySelector('[data-cart-note-save]')?.focus({ preventScroll: true });
      } catch (error) {
        setBusy(false);
        showError(error.message || 'Your note could not be saved.', saveNote);
      }
    };

    drawer.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-cart-quantity-minus]');
      const plus = event.target.closest('[data-cart-quantity-plus]');
      const remove = event.target.closest('[data-cart-remove]');
      const retry = event.target.closest('[data-cart-retry]');
      const noteSave = event.target.closest('[data-cart-note-save]');
      if (retry) { retryAction?.(); return; }
      if (noteSave) { saveNote(); return; }
      const control = minus || plus || remove;
      if (!control) return;
      const key = control.dataset.cartKey;
      const line = control.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-quantity]');
      const current = Number.parseInt(input?.value, 10) || 0;
      if (remove) changeLine(key, 0, 'remove');
      else changeLine(key, Math.max(0, current + (plus ? 1 : -1)), plus ? 'plus' : 'minus');
    });

    drawer.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      changeLine(input.dataset.cartKey, input.value, 'input');
    });

    document.querySelectorAll(`[data-cart-open][aria-controls="${CSS.escape(drawer.id)}"]`).forEach((opener) => {
      opener.addEventListener('click', () => {
        if (!cartState && !busy) fetchCart();
      });
    });

    document.addEventListener('siwa:cart:open', async (event) => {
      if (event.detail?.rendered === true) return;
      const suppliedCart = event.detail?.cart;
      if (suppliedCart) await renderWithSection(suppliedCart, event.detail?.sections?.[itemsSection] || suppliedCart.sections?.[itemsSection]);
      else if (!cartState && !busy) fetchCart();
    });

    document.addEventListener('siwa:cart:updated', async (event) => {
      if (event.detail?.source === 'drawer') return;
      const suppliedCart = event.detail?.cart;
      if (suppliedCart) await renderWithSection(suppliedCart, event.detail?.sections?.[itemsSection] || suppliedCart.sections?.[itemsSection]);
      if (event.detail?.open !== false) {
        document.dispatchEvent(new CustomEvent('siwa:cart:open', { detail: { cart: suppliedCart || null, rendered: Boolean(suppliedCart) } }));
      } else if (!suppliedCart) {
        fetchCart();
      }
    });

    window.SiwaCartDrawer = {
      open: (cart) => document.dispatchEvent(new CustomEvent('siwa:cart:open', { detail: { cart: cart || null } })),
      refresh: fetchCart,
    };
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll?.('[data-cart-drawer]').forEach(initialize);
    if (root.matches?.('[data-cart-drawer]')) initialize(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
